import { DatePipe } from "@angular/common";
import { Component, computed, effect, inject, signal, viewChild } from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { ActivatedRoute, Router } from "@angular/router";
import {
  BehaviorSubject,
  map,
  of,
  timer,
  switchMap,
  takeWhile,
  catchError,
  forkJoin,
  startWith,
  filter,
  tap,
} from "rxjs";
import { ValidationReportStatus, ValidationReportSummary } from "src/app/shared/models/ValidationReport";
import { Report } from "src/app/shared/models/Report";
import { ValidationReportTableComponent } from "src/app/shared/validation-report-table/validation-report-table.component";
import { UpdateNoticeService } from "src/app/update-notice/service/updatenotice.service";
import { setLoader } from "src/app/utils/set-loaders";
import { toSignal } from "@angular/core/rxjs-interop";
import { InfoManagementService } from "../service/info-management.service";
import { Breadcrumb } from "src/app/shared/breadcrumb/breadcrumb.component";
import { ngxCsv } from "ngx-csv";

@Component({
  selector: "app-feature-validation-report",
  standalone: false,
  templateUrl: "./feature-validation-report.component.html",
  styleUrl: "./feature-validation-report.component.scss",
  providers: [DatePipe],
})
export class FeatureValidationReportComponent {
  private _fb = inject(FormBuilder);

  private _datePipe = inject(DatePipe);

  private _router = inject(Router);

  private _activatedRoute = inject(ActivatedRoute);

  private _updateNoticeService = inject(UpdateNoticeService);

  private _infoMgmtService = inject(InfoManagementService);

  private _validationReportTableCmp = viewChild(ValidationReportTableComponent);

  private _pollTimer: number = 5;

  private _loaders$ = new BehaviorSubject({});

  updateNoticeId = this._activatedRoute.snapshot.params["unId"];

  featureId = this._activatedRoute.snapshot.params["featureId"];

  taskId = this._activatedRoute.snapshot.params["taskId"];

  timesliceId = this._activatedRoute.snapshot.params["timesliceId"];

  validationStatus = ValidationReportStatus;

  expandAll = signal(false);

  tableSource = computed(() => this._validationReportTableCmp()?.source());

  rowConfig = [
    {
      key: "date",
      label: "Date",
      accessor: (data: ValidationReportSummary) => {
        return data?.date ? this._datePipe.transform(data.date, "dd MMM yyy") : null;
      },
    },
    {
      key: "result",
      label: "Result",
      accessor: (data: ValidationReportSummary) => {
        return data?.validationReportStatus;
      },
    },
    {
      key: "dataset",
      label: "Dataset",
      accessor: (data: ValidationReportSummary) => {
        return data?.datasetName;
      },
    },
    {
      key: "version",
      label: "Version",
      accessor: (data: ValidationReportSummary) => {
        return data?.versionCode;
      },
    },
    {
      key: "profiles",
      label: "Profiles",
      accessor: (data: ValidationReportSummary) => {
        return data?.schemaList[0]?.name;
      },
    },
  ];

  badgeIconLookup = {
    valid: {
      icon: "check_circle",
      color: "primary",
    },
    invalid: {
      icon: "error",
      color: "warn",
    },
    failed: {
      icon: "cancel",
      color: "warn",
    },
    pending: {
      icon: "schedule",
      color: "accent",
    },
  };

  groupedByConfig = [
    { label: "Severity", value: "flag" },
    { label: "Type", value: "type" },
    { label: "Schema Name", value: "schemaName" },
    { label: "Rule ID ", value: "ruleId" },
    { label: "Context", value: "context" },
    { label: "Diagnostic", value: "errorMessage" },
    { label: "Source", value: "source" },
  ];

  filters = this._fb.group({
    search: [null],
    groupToggle: [false],
    groupBy: [null],
  });

  loaders = toSignal(this._loaders$);

  headerData = toSignal(
    forkJoin({
      updateNotice: this._updateNoticeService.getUpdateNotice(this.updateNoticeId).pipe(catchError(() => of(null))),
      featureGmlName: this._infoMgmtService
        .getFeatureTypeGmlName(this.featureId, this.updateNoticeId)
        .pipe(catchError(() => of(null))),
    }).pipe(startWith(null))
  );

  updateNotice = computed(() => this.headerData()?.updateNotice);

  featureTypeGmlName = computed(() => this.headerData()?.featureGmlName);

  breadCrumb = computed(() => {
    const data = this.headerData();

    if (!data) return null;

    const breadCrumbEntries: Breadcrumb[] = [];
    const { updateNotice, featureGmlName } = data;

    if (updateNotice)
      breadCrumbEntries.push({
        id: "updateNotice",
        label: updateNotice.name,
        routerLink: this._router.createUrlTree(["/change-request/edit", this.updateNoticeId]),
      });

    if (updateNotice && featureGmlName) {
      breadCrumbEntries.push({
        id: "featureGmlName",
        label: `${featureGmlName.featureType} : ${featureGmlName.gmlName}`,
        routerLink: this._router.createUrlTree([
          "/info-management",
          this.featureId,
          this.updateNoticeId,
          updateNotice.name,
          updateNotice.effectiveDate,
          true,
        ]),
      });
    }

    return breadCrumbEntries;
  });

  reportSummary = toSignal(
    timer(0, this._pollTimer * 1000).pipe(
      setLoader(this._loaders$, "getReportSummary"),
      switchMap(() => {
        const unId = this.updateNoticeId;
        const featureId = this.featureId;
        return this._infoMgmtService.getFeatureValidationReport(unId, featureId);
      }),
      catchError(() => of(null)),
      filter((response) => !!response),
      map((result) => ValidationReportSummary.parse(result)),
      tap((report) => console.log(report, "### PARSED REPORT")),
      takeWhile((response) => {
        const isPending = response.validationReportStatus === ValidationReportStatus.PENDING;
        const noStatus = !response.validationReportStatus;
        return isPending || noStatus;
      }, true)
    )
  );

  constructor() {
    effect(() => {
      const isLoading = this.loaders();
      const report = this.reportSummary();
      const isPending = report?.validationReportStatus == ValidationReportStatus.PENDING;
      const disabledState = !report || isPending || isLoading?.["getReportSummary"];
      disabledState ? this.filters.disable() : this.filters.enable();
      console.log(this.breadCrumb(), "### BREADCRUMB DATA");
    });
  }

  onSearch(event: KeyboardEvent): void {
    const value = (event.target as HTMLInputElement).value;
    const tableSource = this.tableSource();
    tableSource.filter = value;
  }

  onGroupToggle(event: MatCheckboxChange): void {
    const checked = event.checked;
    const groupByControl = this.filters.get("groupBy");
    if (checked) {
      const groupBySeverity = this.groupedByConfig[0].value;
      groupByControl.setValue(groupBySeverity);
    } else {
      groupByControl.setValue(null);
    }
  }

  onGroupBySelected(): void {
    this.filters.get("groupToggle").setValue(true);
  }

  toggleExpandAll(): void {
    this.expandAll.update((currentValue) => !currentValue);
  }

  backToHistory(): void {
    //  path: 'info-management/update-notice/:unId/feature/:featureId/validation-history',
    this._router.navigate([
      "/info-management/update-notice",
      this.updateNoticeId,
      "feature",
      this.featureId,
      "validation-history",
    ]);
  }

  exportToCsv(): void {
    const options = {
      fieldSeparator: ",",
      quoteStrings: '"',
      decimalseparator: ".",
      showLabels: true,
      showTitle: false,
      title: "Validation Report",
      useBom: true,
      noDownload: false,
      headers: [
        "Severity",
        "Type",
        "Schema Name",
        "Rule ID",
        "Context",
        "Diagnostic",
        "Rule Source",
        "Suggestion",
        "Location",
        "Feature Identifier",
        "Rule",
        "Source Reference",
        "Schematron",
        "Rule Type",
      ],
    };

    const reportSummary = this.reportSummary();
    const reportListDataCSV = reportSummary.reportList.map((report: Report) => {
      const modifiedMsg = report.errorMessage.replace(/\r?\n|\r|\n/g, "");
      return {
        Severity: report.flag,
        Type: report.type,
        "Schema Name": report.schemaName,
        "Rule ID": report.ruleId,
        Context: report.context,
        Diagnostic: modifiedMsg,
        "Rule Source": report.source,
        Suggestion: report.suggestion,
        Location: report.location,
        "Feature Identifier": report.featureIdentifier,
        Rule: report.rule,
        "Source Reference": report.reference,
        Schematron: report.schematron,
        "Rule Type": report.ruleType,
      };
    });

    new ngxCsv(reportListDataCSV, "Validation Report", options);
  }
}
